import React, { useEffect, useState, useMemo, useCallback} from "react";
import axios from "axios";
import Swal from "sweetalert2";
import useAuth from "../../hooks/useAuth";
import useFetch from "../../hooks/useFetch";
import Pagination from "../../components/Table/pagination";
import { ReactComponent as AnswerCall } from "../../assets/images/svg/AnswerCall.svg";
import { useSIP } from '../../context/SIPContext';
import { useOutletContext } from "react-router-dom";



const CustomerList = (props) => {
  const { auth } = useAuth();
  const fetchData = useFetch();
  const { onlineStatus } = useOutletContext() ?? {};

  const {
    status: sipStatus, callStatus, caller, isHeld, isMuted, remoteAudioRef,
    makeCall, hangup, answer, reject, hold, unhold, mute, unmute
  } = useSIP();

  const permissions = auth?.permissions?.data || [];
  const hasWebRtc = permissions.some(
    (perm) => perm.name === "ContactCenterWebrtc"
  );

  const getCallDisabledReason = (customer) =>
    customer?.dnc
      ? "Customer is marked as DNC. Calls are not allowed"
      : !hasWebRtc
      ? "WebRTC is not enabled for your account. Please contact Admin"
      : onlineStatus !== "available"
      ? "Change your status to Available to make calls"
      : sipStatus !== "connected"
        ? "Sip is not Enabled. Please wait or refresh"
        : "";

  const Initial_pagination = {
    next: "",
    previous: "",
    count: "",
    total_pages: "",
    current_page: 1,
    page_size: 5,
    current_page_count: 0 
  };

  const [customerList, setCustomerList] = useState([]);
  const [selectionMode, setSelectionMode] = useState("");
  const [pagination, setPagination] = useState(Initial_pagination);
  const [selectedCustomersMap, setSelectedCustomersMap] =useState({});
  const [dncValue, setDncValue] = useState("");
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [agentSearch, setAgentSearch] = useState("");
  const [showAgentOptions, setShowAgentOptions] = useState(false);
  const selectedCustomers = useMemo(() => Object.values(selectedCustomersMap),[selectedCustomersMap]);
  const selectableCustomers = useMemo(() => {
    if (selectionMode === "delete") {
      return customerList.filter((customer) => customer.callStatus?.toLowerCase() !== "called");
    }
    if (
      selectionMode === "updateDNC" &&
      selectedCustomers.length > 0
    ) {
      const requiredDnc = Boolean(
        selectedCustomers[0].dnc
      );
      return customerList.filter(
        (customer) =>
          Boolean(customer.dnc) === requiredDnc
      );
    }

    return customerList;
  }, [
    customerList,
    selectionMode,
    selectedCustomers,
  ]);
  
  const isSelectAllChecked = selectableCustomers.length > 0 && selectableCustomers.every((customer) =>Boolean(selectedCustomersMap[customer.id]));

  const clearSelection = useCallback(() => {
      setSelectedCustomersMap({});
      setSelectionMode("");
    }, []);
  
  const startSelectionMode = (mode) => {
    setSelectedCustomersMap({});
    setSelectionMode(mode);
  };

  const handleSelectRow = (customer) => {
    const isAlreadySelected = Boolean(
      selectedCustomersMap[customer.id]
    );

    /*
     * Unselect the customer.
     */
    if (isAlreadySelected) {
      setSelectedCustomersMap((previous) => {
        const updated = { ...previous };

        delete updated[customer.id];

        return updated;
      });

      return;
    }

    const isCalled =
      customer.callStatus?.toLowerCase() ===
      "called";

    if (selectionMode === "delete" && isCalled) {
      return;
    }

    if (
      selectionMode === "updateDNC" &&
      selectedCustomers.length > 0
    ) {
      const firstSelectedDnc = Boolean(
        selectedCustomers[0].dnc
      );

      const currentDnc = Boolean(customer.dnc);

      if (firstSelectedDnc !== currentDnc) {
        Swal.fire({
          icon: "warning",
          title: "Different DNC status",
          text: "Please select customers with the same DNC status.",
        });

        return;
      }
    }

    setSelectedCustomersMap((previous) => ({
      ...previous,
      [customer.id]: customer,
    }));
  };

  const handleSelectAll = (event) => {
    const checked = event.target.checked;
    setSelectedCustomersMap((previous) => {
      const updated = { ...previous };

      if (selectionMode !== "updateDNC") {
        selectableCustomers.forEach((customer) => {
          if (checked) {
            updated[customer.id] = customer;
          } else {
            delete updated[customer.id];
          }
        });

        return updated;
      }

      if (!checked) {
        customerList.forEach((customer) => {
          delete updated[customer.id];
        });
        return updated;
      }

      const alreadySelectedCustomers = Object.values(previous);

      // Use the existing selected customer's DNC value.
      // When nothing is selected, use the first current-page customer's DNC value.
      const referenceCustomer =
        alreadySelectedCustomers[0] || customerList[0];

      if (!referenceCustomer) {
        return updated;
      }

      const requiredDncValue = Boolean(referenceCustomer.dnc);

      customerList.forEach((customer) => {
        if (Boolean(customer.dnc) === requiredDncValue) {
          updated[customer.id] = customer;
        }
      });

      return updated;
    });
  };

  // const updateSelectedDnc = async () => {
  //   if (!selectedCustomers.length) {
  //     Swal.fire({
  //       icon: "warning",
  //       title: "No customers selected",
  //       text: "Please select at least one customer.",
  //     });
  
  //     return;
  //   }
  
  //   const firstDncValue = Boolean(
  //     selectedCustomers[0].dnc
  //   );
  
  //   const hasDifferentDncValues =
  //     selectedCustomers.some(
  //       (customer) =>
  //         Boolean(customer.dnc) !== firstDncValue
  //     );
  
  //   if (hasDifferentDncValues) {
  //     Swal.fire({
  //       icon: "warning",
  //       title: "Different DNC status",
  //       text: "Please select customers with the same DNC status.",
  //     });
  
  //     return;
  //   }
  
  //   const newDncValue = !firstDncValue;
  
  //   const selectedCustomerRows = selectedCustomers
  //     .map(
  //       (customer, index) => `
  //         <tr>
  //           <td style="padding: 8px; border: 1px solid #ddd;">
  //             ${customer.customer_name || "-"}
  //           </td>
  
  //           <td style="padding: 8px; border: 1px solid #ddd;">
  //             ${customer.phone_number || "-"}
  //           </td>
  
  //           <td style="padding: 8px; border: 1px solid #ddd;">
  //             ${customer.campaign_name || "-"}
  //           </td>
  
  //           <td style="padding: 8px; border: 1px solid #ddd;">
  //             ${customer.assigned_agent || "-"}
  //           </td>
  
  //           <td style="padding: 8px; border: 1px solid #ddd;">
  //             ${customer.dnc ? "Yes" : "No"}
  //           </td>
  
  //           <td style="padding: 8px; border: 1px solid #ddd;">
  //             ${newDncValue ? "Yes" : "No"}
  //           </td>
            
  //           <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">
  //             <button
  //               class="remove-customer-btn"
  //               data-id="${customer.id}"
  //               style="
  //                 width: 28px;
  //                 height: 28px;
  //                 background: #ef4444;
  //                 color: white;
  //                 border: none;
  //                 border-radius: 50%;
  //                 cursor: pointer;
  //                 font-size: 12px;
  //                 font-weight: bold;
  //                 line-height: 28px;
  //                 text-align: center;
  //                 padding: 0;
  //                 display: inline-flex;
  //                 align-items: center;
  //                 justify-content: center;
  //               "
  //             >
  //               ✕
  //             </button>
  //           </td>
  //         </tr>
  //       `
  //     )
  //     .join("");

  //   let customersToUpdate = [... selectedCustomers]
    
  //   const result = await Swal.fire({
      
  //     icon: "question",
  //     title: newDncValue
  //       ? "Mark selected customers as DNC?"
  //       : "Remove selected customers from DNC?",
  
  //     html: `
  //       <p id="selected-customer-count" style="margin-bottom: 12px;">
  //         ${selectedCustomers.length} customer(s) will be updated.
  //       </p>
  
  //       <div style="max-height: 320px; overflow: auto;">
  //         <table
  //           style="
  //             width: 100%;
  //             border-collapse: collapse;
  //             font-size: 13px;
  //             text-align: left;
  //           "
  //         >
  //           <thead>
  //             <tr>
  //               <th style="padding: 8px; border: 1px solid #ddd;">
  //                 Customer
  //               </th>
  
  //               <th style="padding: 8px; border: 1px solid #ddd;">
  //                 Phone
  //               </th>
  
  //               <th style="padding: 8px; border: 1px solid #ddd;">
  //                 Campaign
  //               </th>
  
  //               <th style="padding: 8px; border: 1px solid #ddd;">
  //                 Agent
  //               </th>
  
  //               <th style="padding: 8px; border: 1px solid #ddd;">
  //                 Current DNC
  //               </th>
  
  //               <th style="padding: 8px; border: 1px solid #ddd;">
  //                 New DNC
  //               </th>

  //               <th style="padding: 8px; border: 1px solid #ddd;">
  //                 Action
  //               </th>
  //             </tr>
  //           </thead>
  
  //           <tbody>
  //             ${selectedCustomerRows}
  //           </tbody>
  //         </table>
  //       </div>
  //     `,
  
  //     width: "min(1000px, 95vw)",
  //     showCancelButton: true,
  //     confirmButtonText: "Update",
  //     confirmButtonColor: newDncValue
  //       ? "#dc2626"
  //       : "#16a34a",
    
  //   didOpen: () => {
  //       document
  //         .querySelectorAll(".remove-customer-btn")
  //         .forEach((button) => {
  //           button.addEventListener("click", () => {
  //             const customerId = Number(
  //               button.getAttribute("data-id")
  //             );

  //             customersToUpdate = customersToUpdate.filter(
  //               (customer) => customer.id !== customerId
  //             );

  //             setSelectedCustomersMap((prev) => {
  //               const updated = { ...prev };
  //               delete updated[customerId];
  //               return updated;
  //             });

  //             button.closest("tr")?.remove();

  //             const countElement = document.getElementById(
  //               "selected-customer-count"
  //             );

  //             if (countElement) {
  //               countElement.textContent =
  //                 `${customersToUpdate.length} customer(s) will be updated.`;
  //             }

  //             if (customersToUpdate.length === 0) {
  //               Swal.close();
  //             }
  //           });
  //         });
  //     }
  //   });
  //   console.log("CUSTOMERS TO UPDATE :", customersToUpdate);
  //   if (!result.isConfirmed) {
  //     return;
  //   }
  
  //   try {
  //     await axios.post(
  //       process.env.REACT_APP_API_URL + "/contactcentre/update-dnc-status/",
  //       {
  //         customer_ids: customersToUpdate.map(
  //           (customer) => customer.id
  //         ),
  //         dnc: newDncValue,
  //       },
  //       {
  //         headers: {
  //           "Content-Type": "application/json",
  //         },
  //       }
  //     );
  
  //     setCustomerList((previousList) =>
  //       previousList.map((customer) =>
  //         customersToUpdate[customer.id]
  //           ? {
  //               ...customer,
  //               dnc: newDncValue,
  //               is_dnc: newDncValue,
  //             }
  //           : customer
  //       )
  //     );
  
  //     await Swal.fire({
  //       icon: "success",
  //       title: "DNC updated",
  //       text: newDncValue
  //         ? "Selected customers will not be called again."
  //         : "Selected customers are enabled for calling.",
  //     });
  
  //     clearSelection();
  //   } catch (error) {
  //     Swal.fire({
  //       icon: "error",
  //       title: "DNC update failed",
  //       text:
  //         error?.response?.data?.message ||
  //         "Unable to update the selected customers.",
  //     });
  //   }
  // };
  
  const updateSelectedDnc = async () => {
  if (!selectedCustomers.length) {
    Swal.fire({
      icon: "warning",
      title: "No customers selected",
      text: "Please select at least one customer.",
    });
    return;
  }

  const firstDncValue = Boolean(selectedCustomers[0].dnc);

  const hasDifferentDncValues = selectedCustomers.some(
    (customer) =>
      Boolean(customer.dnc) !== firstDncValue
  );

  if (hasDifferentDncValues) {
    Swal.fire({
      icon: "warning",
      title: "Different DNC status",
      text: "Please select customers with the same DNC status.",
    });
    return;
  }

  const newDncValue = !firstDncValue;

  let customersToUpdate = [...selectedCustomers];

  const selectedCustomerRows = selectedCustomers
    .map(
      (customer) => `
        <tr>
          <td style="padding:8px;border:1px solid #ddd;">
            ${customer.customer_name || "-"}
          </td>

          <td style="padding:8px;border:1px solid #ddd;">
            ${customer.phone_number || "-"}
          </td>

          <td style="padding:8px;border:1px solid #ddd;">
            ${customer.campaign_name || "-"}
          </td>

          <td style="padding:8px;border:1px solid #ddd;">
            ${customer.assigned_agent || "-"}
          </td>

          <td style="padding:8px;border:1px solid #ddd;">
            ${customer.dnc ? "Yes" : "No"}
          </td>

          <td style="padding:8px;border:1px solid #ddd;">
            ${newDncValue ? "Yes" : "No"}
          </td>

          <td style="padding:8px;border:1px solid #ddd;text-align:center;">
            <button
              class="remove-customer-btn"
              data-id="${customer.id}"
              style="
                width:28px;
                height:28px;
                background:#ef4444;
                color:white;
                border:none;
                border-radius:50%;
                cursor:pointer;
                font-size:12px;
                font-weight:bold;
                display:flex;
                align-items:center;
                justify-content:center;
                margin:auto;
              "
            >
              ✕
            </button>
          </td>
        </tr>
      `
    )
    .join("");

  const result = await Swal.fire({
    icon: "question",
    title: newDncValue
      ? "Mark selected customers as DNC?"
      : "Remove selected customers from DNC?",

    html: `
      <p id="selected-customer-count" style="margin-bottom:12px;">
        ${selectedCustomers.length} customer(s) will be updated.
      </p>

      <div style="max-height:320px;overflow:auto;">
        <table
          style="
            width:100%;
            border-collapse:collapse;
            font-size:13px;
            text-align:left;
          "
        >
          <thead>
            <tr>
              <th style="padding:8px;border:1px solid #ddd;">Customer</th>
              <th style="padding:8px;border:1px solid #ddd;">Phone</th>
              <th style="padding:8px;border:1px solid #ddd;">Campaign</th>
              <th style="padding:8px;border:1px solid #ddd;">Agent</th>
              <th style="padding:8px;border:1px solid #ddd;">Current DNC</th>
              <th style="padding:8px;border:1px solid #ddd;">New DNC</th>
              <th style="padding:8px;border:1px solid #ddd;">Action</th>
            </tr>
          </thead>

          <tbody>
            ${selectedCustomerRows}
          </tbody>
        </table>
      </div>
    `,

    width: "min(1000px,95vw)",
    showCancelButton: true,
    confirmButtonText: "Update",
    confirmButtonColor: newDncValue
      ? "#dc2626"
      : "#16a34a",

    didOpen: () => {
      document
        .querySelectorAll(".remove-customer-btn")
        .forEach((button) => {
          button.addEventListener("click", () => {
            const customerId = Number(
              button.getAttribute("data-id")
            );

            customersToUpdate = customersToUpdate.filter(
              (customer) => customer.id !== customerId
            );

            setSelectedCustomersMap((prev) => {
              const updated = { ...prev };
              delete updated[customerId];
              return updated;
            });

            button.closest("tr")?.remove();

            const countElement =
              document.getElementById(
                "selected-customer-count"
              );

            if (countElement) {
              countElement.textContent = `${customersToUpdate.length} customer(s) will be updated.`;
            }

            if (customersToUpdate.length === 0) {
              Swal.close();
            }
          });
        });
    },
  });

  if (!result.isConfirmed) {
    return;
  }

  if (customersToUpdate.length === 0) {
    Swal.fire({
      icon: "warning",
      title: "No customers selected",
      text: "Please select at least one customer.",
    });
    return;
  }

  try {
    const res_data = await fetchData(
        "post",
        {
          customer_ids: customersToUpdate.map(
            (customer) => customer.id
          ),
          dnc: newDncValue,
        },
        `${process.env.REACT_APP_API_URL}/contactcentre/update-dnc-status/`
      );
    // await axios.post(
    //   `${process.env.REACT_APP_API_URL}/contactcentre/update-dnc-status/`,
    //   {
    //     customer_ids: customersToUpdate.map(
    //       (customer) => customer.id
    //     ),
    //     dnc: newDncValue,
    //   },
    //   {
    //     headers: {
    //       "Content-Type": "application/json",
    //       Authorization: `Bearer ${auth?.accessToken}`,
    //     },
    //   }
    // );

    const updatedIds = new Set(
      customersToUpdate.map(
        (customer) => customer.id
      )
    );

    setCustomerList((previousList) =>
      previousList.map((customer) =>
        updatedIds.has(customer.id)
          ? {
              ...customer,
              dnc: newDncValue,
              is_dnc: newDncValue,
            }
          : customer
      )
    );

    await Swal.fire({
      icon: "success",
      title: "DNC updated",
      text: newDncValue
        ? "Selected customers will not be called again."
        : "Selected customers are enabled for calling.",
    });

    clearSelection();
  } catch (error) {
    Swal.fire({
      icon: "error",
      title: "DNC update failed",
      text:
        error?.response?.data?.message ||
        "Unable to update the selected customers.",
    });
  }
};

  const PaginationControl = (type, number) => {
    if (type == "page") {
      if (number <= pagination.total_pages) {
        fetchCampainCustomers(number);
      } else {
        fetchCampainCustomers(1);
      }
    } else if (type == "last") {
      fetchCampainCustomers(pagination.total_pages);
    } else if (type == "prev") {
      if (pagination.current_page > 1) {
        fetchCampainCustomers(pagination.current_page - 1);
      }
    } else if (type == "next") {
      if (pagination.current_page < pagination.total_pages) {
        fetchCampainCustomers(pagination.current_page + 1);
      }
    } else {
      fetchCampainCustomers(1);
    }
  };
  
  const [searchText, setSearchText] = useState("");
  const [campaignFilter, setCampaignFilter] = useState("");
  const [status, setStatus] = useState("");
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [showCampaignOptions, setShowCampaignOptions] = useState(false);

  const searchSubmit = () => {
    fetchCampainCustomers(1);
  };
  
  const fetchCampaignList = async () => {
    let params = {
      user_id: auth?.user_details?.user,
      user_type: props.user_type,
    };

    try {
      const response = await axios.get(
        process.env.REACT_APP_API_URL + "/contactcentre/all-campaigns/",
        {
          params,
        }
      );
      setAllCampaigns(response.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await fetchData(
        "get",
        {},
        `${process.env.REACT_APP_API_URL}/usermanagement/user-list-dropdown/`
      );
      setAgents(response);
    } catch (err) {
      console.error("Error fetching agents:", err);
    }
  };

  const fetchCampainCustomers = async (pagenumber) => {
    let postData = {
      user_id: auth?.user_details?.user,
      user_type: props.user_type,
      page: pagenumber,
      search: searchText,
      version: "v2",
    };

    if (campaignFilter) {
      postData.campaign__name = campaignFilter;
    }

    if (status.length > 0) {
      postData.callStatus = status;
    }
    if (dncValue !== "") {
      postData.dnc = dncValue;
    }
    if (selectedAgent !== "") {
      postData.user_filter   = selectedAgent;
    }

    let config = {
      headers: { "Content-Type": "application/json" },
    };

    await axios
      .get(
        process.env.REACT_APP_API_URL + "/contactcentre/customer-list/",
        {
          params: postData,
        },
        config
      )
      .then((res) => {
        console.log("CAMPAIGN CUSTOMER LIST   : ", res);
        setCustomerList(res.data.results);
        setPagination((prevState) => {
          return {
            ...prevState,
            total_pages: res.data?.total_pages || 0,
            previous: res.data?.previous || null,
            next: res.data?.next || null,
            current_page: res.data?.current_page || 0,
            count: res.data?.count || 0,
            page_size: res.data?.page_size || 5,
            current_page_count: res.data?.results?.length || 0 
          };
        });
      });
  };


  const callCustomer = (customer_number, customer_name, campaign_name) => {
    if (customer_number) {
      const callerDisplay = customer_name
        ? `${customer_name} (${customer_number})${campaign_name ? ' - ' + campaign_name : ''}`
        : customer_number;

      // setCaller(callerDisplay);
      makeCall(customer_number);
    }
  };
  
  useEffect(() => {
    fetchCampaignList();
    fetchCampainCustomers(1);
    fetchAgents();
    console.log("PROPS :", props);
  }, []);

  return (
    <>
      <h1 className="font-bold text-lg">Customer List</h1>
      <div className="mt-3 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
        
        {/* Filters */}
        <div className="flex flex-wrap items-start gap-3 flex-1">
          <div className="relative w-full sm:w-56 md:w-64">
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-full h-10 px-3 pr-10 text-sm bg-gray-50 border border-gray-300 rounded-lg"
              placeholder="Search customers"
            />
            {searchText && (
              <button
                type="button"
                onClick={() => setSearchText("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
              >
                ×
              </button>
            )}
          </div>

          <div className="relative w-full sm:w-56 md:w-64">
            <input
              type="text"
              value={campaignFilter}
              onFocus={() => setShowCampaignOptions(true)}
              onBlur={() => setTimeout(() => setShowCampaignOptions(false), 150)}
              onChange={(e) => {
                setCampaignFilter(e.target.value);
                setShowCampaignOptions(true);
              }}
              className="w-full h-10 px-3 pr-10 text-sm bg-gray-50 border border-gray-300 rounded-lg"
              placeholder="Select campaign"
            />

            {showCampaignOptions && (
              <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg">
                <div
                  className="cursor-pointer px-3 py-2 text-sm hover:bg-gray-100"
                  onMouseDown={() => {
                    setCampaignFilter("");
                    setShowCampaignOptions(false);
                  }}
                >
                  All Campaigns
                </div>

                {allCampaigns
                  ?.filter((campaign) => {
                    const name =
                      campaign.name ||
                      campaign.campaign_name ||
                      campaign.label ||
                      "";
                    return name
                      .toLowerCase()
                      .includes((campaignFilter || "").toLowerCase());
                  })
                  .map((campaign) => {
                    const name =
                      campaign.name ||
                      campaign.campaign_name ||
                      campaign.label ||
                      "";

                    return name ? (
                      <div
                        key={`${campaign.id || campaign.campaign_id || name}-${name}`}
                        className="cursor-pointer px-3 py-2 text-sm hover:bg-gray-100"
                        onMouseDown={() => {
                          setCampaignFilter(name);
                          setShowCampaignOptions(false);
                        }}
                      >
                        {name}
                      </div>
                    ) : null;
                  })}
              </div>
            )}

            {campaignFilter && (
              <button
                type="button"
                onClick={() => setCampaignFilter("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
              >
                ×
              </button>
            )}
          </div>

          <div className="relative w-full sm:w-48 md:w-56">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full h-10 px-3 pr-10 text-sm bg-gray-50 border border-gray-300 rounded-lg appearance-none"
            >
              <option value="">Filter by status</option>
              <option value="called">Called</option>
              <option value="notcalled">Not Called</option>
            </select>

            {status && (
              <button
                type="button"
                onClick={() => setStatus("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
              >
                ×
              </button>
            )}
          </div>
          
          <div className="relative w-full sm:w-48 md:w-56">
            <select
              value={dncValue}
              onChange={(event) => setDncValue(event.target.value)}
              className="h-10 w-full sm:w-48 md:w-56 rounded-md border border-gray-300 bg-white px-3 text-sm"
            >
              <option value="">DNC Status</option>
              <option value={true}>Yes</option>
              <option value={false}>No</option>
            </select>
          </div>

          <div className="relative w-full sm:w-48 md:w-56">
            <input
              type="text"
              value={agentSearch}
              onFocus={() => setShowAgentOptions(true)}
              onBlur={() => setTimeout(() => setShowAgentOptions(false), 150)}
              onChange={(event) => {
                setAgentSearch(event.target.value);
                setSelectedAgent("");
                setShowAgentOptions(true);
              }}
              placeholder="Search agent"
              className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 pr-9 text-sm"
              role="combobox"
              aria-expanded={showAgentOptions}
              aria-controls="agent-options"
              aria-autocomplete="list"
            />

            {showAgentOptions && (
              <div
                id="agent-options"
                className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg"
                role="listbox"
              >
                <div
                  className="cursor-pointer px-3 py-2 text-sm hover:bg-gray-100"
                  onMouseDown={() => {
                    setSelectedAgent("");
                    setAgentSearch("");
                    setShowAgentOptions(false);
                  }}
                  role="option"
                  aria-selected={!selectedAgent}
                >
                  All Agents
                </div>

                {agents
                  .filter((agent) =>
                    (agent.profile_name || "")
                      .toLowerCase()
                      .includes(agentSearch.toLowerCase())
                  )
                  .map((agent) => (
                    <div
                      key={agent.id}
                      className="cursor-pointer px-3 py-2 text-sm hover:bg-gray-100"
                      onMouseDown={() => {
                        setSelectedAgent(String(agent.id));
                        setAgentSearch(agent.profile_name || "");
                        setShowAgentOptions(false);
                      }}
                      role="option"
                      aria-selected={selectedAgent === String(agent.id)}
                    >
                      {agent.profile_name}
                    </div>
                  ))}

                {agents.filter((agent) =>
                  (agent.profile_name || "")
                    .toLowerCase()
                    .includes(agentSearch.toLowerCase())
                ).length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    No agents found
                  </div>
                )}
              </div>
            )}

            {agentSearch && (
              <button
                type="button"
                onClick={() => {
                  setSelectedAgent("");
                  setAgentSearch("");
                  setShowAgentOptions(false);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                aria-label="Clear agent filter"
              >
                ×
              </button>
            )}
          </div>
          

          <button
            className="h-10 w-[100px] bg-blue-500 hover:bg-blue-700 text-white font-semibold px-5 rounded"
            onClick={searchSubmit}
          >
            Search
          </button>
        </div>

        {/* Right Side Buttons */}
        <div className="flex flex-wrap justify-start lg:justify-end gap-2">
          {selectionMode ? (
            <>
              <button
                type="button"
                onClick={clearSelection}
                className="h-10 rounded-md bg-gray-600 px-5 text-sm font-semibold text-white hover:bg-gray-700"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={selectedCustomers.length === 0}
                onClick={updateSelectedDnc}
                className="h-10 rounded-md bg-amber-600 px-5 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
              >
                Update DNC ({selectedCustomers.length})
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => startSelectionMode("updateDNC")}
              className="h-10 rounded-md bg-amber-600 px-5 text-sm font-semibold text-white hover:bg-amber-700"
            >
              Update DNC
            </button>
          )}
        </div>
      </div>
      <div className="overflow-x-auto p-4 relative shadow-md sm:rounded-lg h-[71.8vh]">
        
        <table className="w-full text-sm text-left text-gray-800 ">
          <thead className="text-xs text-center text-gray-700 uppercase bg-gray-50 d">
            <tr>
              <th scope="col" className="py-3 px-6">
                Sl-No
              </th>
              <th scope="col" className="py-3 px-6">
                Customer
              </th>
              <th scope="col" className="py-3 px-6">
                Campaign
              </th>
              <th scope="col" className="py-3 px-6">
                Phone No
              </th>
              {hasWebRtc ?
                <th scope="col" className="py-3 px-6">
                  Call
                </th> : <></>}
              <th scope="col" className="py-3 px-6">
                Status
              </th>
              <th className="w-[8%] px-3 py-3">
                DNC
              </th>
              {selectionMode && (
                <th className="w-[5%] px-3 py-3">
                  <input
                    type="checkbox"
                    checked={isSelectAllChecked}
                    onChange={handleSelectAll}
                    aria-label="Select all customers on the current page"
                    className="h-4 w-4 cursor-pointer"
                  />
                </th>
              )}
            </tr>
          </thead>
          <tbody className="text-center">
            {Array.isArray(customerList) && customerList.length > 0 ? (
              customerList.map((customer, index) => {
                const isRowSelected = Boolean(selectedCustomersMap[customer.id]);

                return (
                  <tr
                    key={customer.id}
                    className="bg-white border-b hover:bg-gray-50"
                  >
                    <td className="py-4 px-6">
                      {index +
                        1 +
                        (pagination?.current_page - 1) * pagination?.page_size}
                    </td>
                    <td className="py-4 px-6">{customer.customer_name}</td>
                    <td className="py-4 px-6">{customer.campaign_name}</td>
                    <td className="py-4 px-6">{customer.phone_number}</td>
                    {hasWebRtc ? (() => {
                    const callDisabledReason = getCallDisabledReason(customer);
                    const canCall = !callDisabledReason;
                    return (
                    <td className="py-4 px-6">
                      <div className="group relative inline-flex justify-center">
                        <button
                          className={`${!canCall ? "opacity-50 cursor-not-allowed " : ""}hover:bg-green-200 text-white rounded-full`}
                          disabled={!canCall}
                          aria-label={canCall ? "Call customer" : callDisabledReason}
                          onClick={() => callCustomer(customer.phone_number)}
                        >
                          <AnswerCall className="w-3 h-3 sm:w-10 sm:h-10" />
                        </button>
                        {!canCall && (
                          <span
                            role="tooltip"
                            className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-3 -translate-x-1/2 translate-y-1 scale-95 whitespace-nowrap rounded-lg bg-gray-900/95 px-3 py-1.5 text-xs font-medium text-white opacity-0 shadow-lg ring-1 ring-white/10 transition-all duration-200 ease-out group-hover:translate-y-0 group-hover:scale-100 group-hover:opacity-100"
                          >
                            {callDisabledReason}
                            <span className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-900/95" />
                          </span>
                        )}
                      </div>
                    </td>
                    );
                    })() : <></>}
                    <td className="py-4 px-6">{customer.status}</td>
                    <td className="px-3 py-4">
                      <span
                        className={`inline-flex items-center justify-center rounded-full border px-3 py-1 text-xs font-semibold ${
                          customer.dnc
                            ? "border-red-200 bg-red-100 text-red-700"
                            : "border-green-200 bg-green-100 text-green-700"
                        }`}
                      >
                        {customer.dnc ? "Yes" : "No"}
                      </span>
                    </td>
                    {selectionMode && (
                      <td className="px-3 py-4">
                        <input
                          type="checkbox"
                          checked={isRowSelected}
                          onChange={() => handleSelectRow(customer)}
                          aria-label={`Select ${
                            customer.customer_name || "customer"
                          }`}
                          className="h-4 w-4 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                        />
                      </td>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={
                    7 +
                    (hasWebRtc ? 1 : 0) +
                    (selectionMode ? 1 : 0)
                  }
                  className="py-8 text-center text-gray-500"
                >
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div >
      {
        props.edit_fb && (
          <div className="p-8">
            <Pagination
              pagination_data={pagination}
              PaginationControl={PaginationControl}
            />
          </div>
        )
      }
    </>
  );
};

export default CustomerList;
